import React, { useEffect, useState } from "react";
import { TopNav } from "@/components/TopNav";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { RankBadge } from "@/components/RankBadge";
import { Target, Swords, Coffee, Zap, Power, Sparkles, X, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const PRESENCE = [
  { key: "ready_rank", label: "Muốn đấu Rank", icon: Swords, color: "#00e5ff" },
  { key: "ready_friendly", label: "Đấu giao hữu", icon: Sparkles, color: "#a855f7" },
  { key: "resting", label: "Đang nghỉ", icon: Coffee, color: "#ffb020" },
  { key: "offline", label: "Tắt trạng thái", icon: Power, color: "#8d92a3" },
];

export default function FindOpponent() {
  const { user, refresh } = useAuth();
  const [opponents, setOpponents] = useState([]);
  const [presence, setPresence] = useState(user?.presence || "offline");
  const [challengeFor, setChallengeFor] = useState(null);

  const load = () => api.get("/find-opponent?elo_range=500").then(({ data }) => setOpponents(data));

  useEffect(() => {
    load();
    const i = setInterval(load, 8000);
    return () => clearInterval(i);
  }, []);

  const setMyPresence = async (status) => {
    try {
      await api.post("/me/presence", { status });
      setPresence(status);
      await refresh();
      toast.success(`Trạng thái: ${PRESENCE.find((p) => p.key === status)?.label}`);
    } catch (e) { toast.error(e?.response?.data?.detail); }
  };

  return (
    <div className="min-h-screen bg-[#05050a]">
      <TopNav />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Target className="text-purple-300" />
          <h1 className="font-display font-bold text-3xl sm:text-4xl">Tìm đối thủ</h1>
        </div>

        {/* Presence toggle */}
        <div className="rounded-xl bg-[#0c0d14] border border-white/8 p-5 mb-6">
          <div className="text-xs uppercase tracking-wider text-white/50 mb-3">Trạng thái của bạn</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {PRESENCE.map((p) => (
              <button key={p.key} onClick={() => setMyPresence(p.key)} data-testid={`presence-${p.key}`}
                className={`p-3 rounded-md border flex items-center gap-2 text-sm transition ${presence === p.key ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-300" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05] text-white/70"}`}>
                <p.icon size={15} style={{ color: presence === p.key ? "#00e5ff" : p.color }} />
                {p.label}
              </button>
            ))}
          </div>
          <div className="mt-3 text-[11px] text-white/40">Bật "Muốn đấu" để xuất hiện trong danh sách tìm đối thủ của người khác.</div>
        </div>

        <h2 className="font-display font-semibold mb-3 flex items-center gap-2">
          <Zap size={16} className="text-cyan-300" /> Gợi ý đối thủ
          <span className="text-xs text-white/40 font-normal">· cùng trình độ · đang sẵn sàng</span>
        </h2>

        {opponents.length === 0 ? (
          <div className="rounded-xl bg-[#0c0d14] border border-white/8 p-12 text-center text-white/40">
            Chưa có ai sẵn sàng. Hãy bật trạng thái "Muốn đấu" và rủ bạn bè vào!
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {opponents.map((p) => (
              <div key={p.id} className="rounded-xl bg-[#0c0d14] border border-white/8 p-4 hover:border-cyan-400/30 transition" data-testid={`opponent-${p.id}`}>
                <div className="flex items-center gap-3 mb-3">
                  <img src={p.avatar_url} alt="" className="w-12 h-12 rounded-full border border-white/10" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold truncate">{p.nickname}</div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <RankBadge elo={p.elo} size="sm" showElo={false} />
                      <span className="text-[10px] text-white/50">Δ {p.elo_diff > 0 ? "+" : ""}{p.elo_diff}</span>
                    </div>
                  </div>
                </div>
                <div className="text-xs text-white/50 mb-3">
                  <span className="text-cyan-300">{p.wins}</span>W / <span className="text-red-400">{p.losses}</span>L · {p.win_rate}%
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setChallengeFor(p)} data-testid={`challenge-${p.id}`}
                    className="flex-1 px-3 py-2 rounded-md text-xs font-medium bg-gradient-to-r from-cyan-400 to-purple-500 text-black flex items-center justify-center gap-1.5">
                    <Swords size={12} /> Thách đấu
                  </button>
                  <Link to={`/chat/dm/${p.id}`} className="px-3 py-2 rounded-md text-xs border border-white/10 hover:bg-white/5 flex items-center gap-1.5">
                    <MessageCircle size={12} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {challengeFor && <ChallengeModal target={challengeFor} onClose={() => setChallengeFor(null)} />}
    </div>
  );
}

export const ChallengeModal = ({ target, onClose }) => {
  const [bo, setBo] = useState(1);
  const [mode, setMode] = useState("rank");
  const [msg, setMsg] = useState("");
  const submit = async () => {
    try {
      await api.post("/challenges", { receiver_id: target.id, best_of: Number(bo), mode, message: msg });
      toast.success(`Đã gửi thách đấu tới ${target.nickname}`);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-[#0c0d14] border border-white/10 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold flex items-center gap-2"><Swords size={16} className="text-cyan-300" /> Thách đấu</h3>
          <button onClick={onClose} className="p-1 hover:bg-white/5 rounded"><X size={16} /></button>
        </div>
        <div className="flex items-center gap-3 mb-4 p-3 rounded-md bg-white/[0.03] border border-white/10">
          <img src={target.avatar_url} alt="" className="w-10 h-10 rounded-full" />
          <div>
            <div className="font-display font-semibold">{target.nickname}</div>
            <RankBadge elo={target.elo} size="sm" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1.5">Best Of</div>
            <select value={bo} onChange={(e) => setBo(e.target.value)} data-testid="challenge-bo" className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm">
              <option value={1}>BO1</option><option value={3}>BO3</option><option value={5}>BO5</option>
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1.5">Chế độ</div>
            <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="challenge-mode" className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm">
              <option value="rank">Rank</option><option value="friendly">Giao hữu</option>
            </select>
          </div>
        </div>
        <label className="block mb-4">
          <span className="text-[10px] uppercase tracking-wider text-white/50">Tin nhắn (tùy chọn)</span>
          <input value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="challenge-msg" maxLength={200} placeholder="vd: Chiến không?" className="mt-1 w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm outline-none focus:border-cyan-400/60" />
        </label>
        <button onClick={submit} data-testid="send-challenge" className="w-full py-2.5 rounded-md font-display font-semibold text-sm bg-gradient-to-r from-cyan-400 to-purple-500 text-black flex items-center justify-center gap-2">
          <Swords size={15} /> Gửi thách đấu
        </button>
      </div>
    </div>
  );
};
